import { Outlet, useLocation, useNavigate } from "@tanstack/react-router"
import type { ReactNode } from "react"
import { useCallback, useEffect } from "react"

import { useText } from "../i18n/use-text"
import { accessStore } from "../access/access-store"
import { useCabinetAccess } from "../access/use-access"
import { endImpersonation } from "../api/auth.api"
import { formatDateLong, todayInZone, viewerTimeZone } from "../lib/format"
import styles from "./app-shell.module.css"
import { BranchSwitcher } from "./branch-switcher"
import { buildStripes } from "./build-stripes"
import { SuspendedScreen } from "../screens/tenant/suspended-screen"
import { markFreshNow, useDataAt } from "./data-freshness"
import { hasSideNav } from "./layout"
import { NavIcons } from "./nav-icons"
import { useNavBadges } from "./nav-badges"
import { useEventsBadge } from "../screens/notifications/use-events-badge"
import { CABINET_PATHS, sectionByPath } from "./nav-items"
import { NavSidebar } from "./nav-sidebar"
import { NavTabbar } from "./nav-tabbar"
import { ServiceStripes } from "./service-stripes"
import "./shell-tokens.css"
import { buildTimezoneNote, formatDataFreshness } from "./timezone-note"
import { TimezoneStripe } from "./timezone-stripe"
import { Topbar } from "./topbar"
import { useShellBranchSelection } from "./use-shell-branch"
import { useBranchPicker } from "./use-branch-navigation"
import { useMinuteTick } from "./use-minute"
import { buildNavEntries } from "./use-nav"
import { useOnline } from "./use-online"
import { useShellLayout } from "./use-viewport"

/**
 * Оболочка кабинета: полосы состояния, навигация, шапка и рабочая область.
 *
 * Ставится ПОСЛЕ проверки доступа, поэтому профиль здесь есть по построению —
 * useCabinetAccess не возвращает «может быть». Ни один экран внутри не обязан
 * заново различать «грузится» и «загружено».
 *
 * ТРИ РАСКЛАДКИ — ОДНО ДЕРЕВО ДАННЫХ. Список разделов, полосы, филиал и свежесть данных
 * считаются здесь один раз и одинаково; меняется только то, чем их рисуют. Иначе
 * администратор, открывший кабинет с телефона, увидел бы другой набор разделов, чем
 * за стойкой, и решил бы, что у него отобрали права.
 */
export function AppShell(): ReactNode {
  const access = useCabinetAccess()
  const t = useText()
  const layout = useShellLayout()
  const online = useOnline()
  const navigate = useNavigate()
  const dataAt = useDataAt()
  const badges = useNavBadges()
  const now = useMinuteTick()

  // Два отдельных отбора, а не location целиком: оболочка перерисовывается при смене
  // адреса, а не при каждом внутреннем обновлении маршрутизатора.
  const pathname = useLocation({ select: (location) => location.pathname })

  const context = access.context
  const section = sectionByPath(pathname)
  const pickBranch = useBranchPicker(context)

  // Выбор филиала — той же функцией, которой его берут разделы без своего параметра
  // (центр уведомлений): шапка и экран обязаны говорить об одной точке.
  const allowsAll = section?.allowsAllBranches ?? true
  const selection = useShellBranchSelection()
  const branch = selection.kind === "one" ? selection.branch : null
  const timeZone = branch?.timezone ?? context.tenant.defaultTimezone

  /*
   * Счётчик дел центра уведомлений — для колокольчика и для пункта меню. Спрашивает
   * ОБОЛОЧКА, а не экран: колокольчик стоит в шапке всегда, а лента открыта минуту в день
   * (см. use-events-badge.ts). Филиал в нём тот же, что выбран в шапке: дела чужой точки
   * в этот момент не его.
   */
  useEventsBadge(branch?.id)

  // Загрузка профиля — первый успешный ответ сервера за сессию. Отмечаем её как момент
  // свежести, чтобы полоса «Нет связи», выпавшая до открытия журнала, всё равно могла
  // назвать время, а не отделываться словами «данные не загружены».
  useEffect(() => {
    markFreshNow()
  }, [context])

  const onRetry = useCallback(() => {
    void accessStore.reload()
  }, [])

  const onExitImpersonation = useCallback(() => {
    void endImpersonation().finally(() => {
      accessStore.markAnonymous("signed_out")
    })
  }, [])

  const onPay = useCallback(() => {
    void navigate({ to: CABINET_PATHS.billing })
  }, [navigate])

  const onSupport = useCallback(() => {
    void navigate({ to: CABINET_PATHS.support })
  }, [navigate])

  const onSignOut = useCallback(() => {
    void accessStore.signOut()
  }, [])

  const onChooseSalon = useCallback(() => {
    void navigate({ to: "/choose-salon" })
  }, [navigate])

  /*
   * ОГРАНИЧЕННЫЙ ДОСТУП — ЭКРАН ВМЕСТО КАБИНЕТА, И ПРОВЕРКА СТОИТ ДО ВСЕГО ОСТАЛЬНОГО.
   *
   * Сервер отказывает каждому запросу, кроме выгрузки базы, выхода и оплаты. Кабинет
   * с навигацией и нерабочими кнопками человек читает как сломанный и звонит нам;
   * поэтому ни полосы, ни шапки, ни разделов здесь нет — только объяснение и то,
   * что действительно работает.
   *
   * `grace` сюда НЕ попадает: это «оплата просрочена, доступ ещё есть», и о нём говорит
   * полоса, а не экран.
   */
  const restricted = context.tenant.status === "suspended" || context.tenant.status === "archived"
  /*
   * ДВА АДРЕСА ПРОПУСКАЮТСЯ И В ОГРАНИЧЕННОМ РЕЖИМЕ: оплата и поддержка. Иначе кнопка
   * «Оплатить» вела бы в этот же экран, то есть в никуда, — а ограничение превратилось бы
   * в ловушку, из которой нельзя даже заплатить.
   */
  const escapeHatch = pathname === CABINET_PATHS.billing || pathname === CABINET_PATHS.support

  if (restricted && !escapeHatch) {
    return (
      <div className={styles.shell}>
        <SuspendedScreen
          status={context.tenant.status === "archived" ? "archived" : "suspended"}
          tenantName={context.tenant.name}
          roleCode={context.role.code}
          canExport={access.permissions.has("clients.export")}
          onPay={onPay}
          onSupport={onSupport}
          onSignOut={onSignOut}
          onMyTenants={onChooseSalon}
        />
      </div>
    )
  }

  const stripes = buildStripes(
    {
      online,
      dataAt,
      timeZone,
      impersonation: context.impersonation,
      tenantName: context.tenant.name,
      tenantStatus: context.tenant.status,
      // Срока отсрочки и конца триала в API сегодня нет — см. комментарий в build-stripes.
      billingDeadline: null,
    },
    { onRetry, onExitImpersonation, onPay },
  )

  // Полоса и точка говорят об одном и том же. Точка зажигается только тогда, когда
  // полосы на экране нет: одно и то же не сообщается дважды, иначе перестают замечать оба.
  const billingStripeShown = stripes.some((stripe) => stripe.kind === "billing")
  const billingAttention = context.tenant.status === "grace" || context.tenant.status === "trial"

  const entries = buildNavEntries({
    permissions: access.permissions,
    pathname,
    badges,
    billingDot: billingAttention && !billingStripeShown,
  })

  const viewerZone = viewerTimeZone()
  const zoneNote =
    branch === null
      ? null
      : buildTimezoneNote({
          branchName: branch.name,
          branchTimeZone: branch.timezone,
          viewerTimeZone: viewerZone,
          now,
        })

  const branchSwitcher = (
    <BranchSwitcher
      branches={context.branches}
      selection={selection}
      allowsAll={allowsAll}
      allDeniedReason={
        allowsAll
          ? undefined
          : "В журнале «Все филиалы» не бывает: у точек разные часовые пояса, и день двух филиалов в одной сетке нечитаем"
      }
      viewerTimeZone={viewerZone}
      now={now}
      onPick={pickBranch}
      filled={layout === "compact"}
    />
  )

  const topbar = (
    <Topbar
      layout={layout}
      /*
       * Профиль — не раздел меню, но у страницы есть имя: общее «Кабинет» в шапке
       * профиля не говорило человеку, где он, и путало с корнем кабинета.
       */
      title={
        section === null || section === undefined
          ? pathname === "/profile" || pathname.startsWith("/profile/")
            ? t("shell.profile")
            : t("shell.cabinet")
          : t(section.textKey)
      }
      subtitle={`${formatDateLong(todayInZone(timeZone), timeZone)} · ${branch?.name ?? context.tenant.name}`}
      freshness={formatDataFreshness(dataAt, timeZone)}
      branch={branchSwitcher}
      /*
       * КОЛОКОЛЬЧИК ВЕДЁТ В ЛЕНТУ И НЕСЁТ ЧИСЛО ДЕЛ. Пока экрана не было, он стоял
       * выключенным с объяснением — теперь объяснять нечего, кроме самого числа.
       * Счётчик один и тот же у колокольчика и у пункта меню: он лежит в nav-badges,
       * и обновляет его тот, кто последним спрашивал сервер (оболочка раз в пять минут,
       * открытая лента — на каждое действие).
       */
      {...(badges.notifications === undefined ? {} : { notifications: badges.notifications })}
      onOpenNotifications={() => {
        void navigate({ to: CABINET_PATHS.notifications, search: {} })
      }}
    />
  )

  const head = (
    <>
      <ServiceStripes stripes={stripes} />
      {topbar}
      {zoneNote === null ? null : <TimezoneStripe note={zoneNote} />}
    </>
  )

  if (!hasSideNav(layout)) {
    return (
      <div className={styles.shell}>
        {head}
        {/* Филиал виден и на телефоне (ТЗ §4.4): переключатель уезжает ПОД ШАПКУ,
            но не исчезает — записать клиента не в тот город можно и отсюда.
            Стоял он после рабочей области, то есть внизу, между кнопкой «Записать»
            и вкладками: три полосы друг на друге в зоне большого пальца, и промах
            по «Записать» открывал выбор филиала. */}
        <div className={styles.phoneBranch}>{branchSwitcher}</div>
        <main className={styles.content}>
          <Outlet />
        </main>
        <NavTabbar
          entries={entries}
          userName={context.user.displayName}
          roleName={context.role.name}
        />
      </div>
    )
  }

  return (
    <div className={styles.shell}>
      <div className={styles.body}>
        {layout === "compact" ? (
          <NavIcons
            entries={entries}
            tenantName={context.tenant.name}
            userName={context.user.displayName}
            roleName={context.role.name}
          />
        ) : (
          <NavSidebar
            entries={entries}
            tenantName={context.tenant.name}
            userName={context.user.displayName}
            roleName={context.role.name}
          />
        )}

        <div className={styles.main}>
          {head}
          <main className={styles.content}>
            <Outlet />
          </main>
        </div>
      </div>
    </div>
  )
}
